import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import Alert from './Alert';
import PermissionGate from './PermissionGate';
import {
  createCrmUserExportDownloadUrl,
  fetchCrmUserExports,
} from '../services/userCrmApi';

const activeStatuses = new Set(['queued', 'running']);

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFileSize(bytes) {
  const parsed = Number(bytes || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return '-';
  if (parsed < 1024) return `${parsed} B`;
  if (parsed < 1024 * 1024) return `${(parsed / 1024).toFixed(1)} KB`;
  return `${(parsed / (1024 * 1024)).toFixed(1)} MB`;
}

function statusLabel(status) {
  if (status === 'queued') return 'Queued';
  if (status === 'running') return 'Running';
  if (status === 'completed') return 'Completed';
  if (status === 'failed') return 'Failed';
  return status || '-';
}

function statusClasses(status, expired) {
  if (expired) return 'bg-gray-100 text-gray-700 border-gray-200';
  if (status === 'completed') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'failed') return 'bg-red-50 text-red-700 border-red-200';
  if (status === 'running') return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

function isExpired(job) {
  const expiresAt = parseDate(job.expiresAt);
  return Boolean(expiresAt && expiresAt.getTime() <= Date.now());
}

function progressText(job) {
  const exported = Number(job.exportedCount || 0);
  const total = Number(job.totalCount || 0);
  if (total > 0) return `${exported.toLocaleString('en-IN')} / ${total.toLocaleString('en-IN')}`;
  if (exported > 0) return exported.toLocaleString('en-IN');
  return job.status === 'queued' ? 'Waiting' : '-';
}

function CrmDownloads() {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  const hasActiveJobs = useMemo(
    () => exports.some((job) => activeStatuses.has(job.status)),
    [exports]
  );

  const loadExports = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await fetchCrmUserExports();
      setExports(result.exports || []);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to load CRM downloads.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  useEffect(() => {
    if (!hasActiveJobs) return undefined;
    const interval = window.setInterval(() => {
      loadExports({ silent: true });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [hasActiveJobs, loadExports]);

  const handleDownload = async (job) => {
    setDownloadingId(job.id);
    try {
      const result = await createCrmUserExportDownloadUrl(job.id);
      if (result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setAlertMessage(error.message || 'Failed to create download link.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-2xl">
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">CRM Downloads</h2>
          <p className="text-gray-500 mt-1">Async exports generated from App Users CRM</p>
        </div>
        <button
          type="button"
          onClick={() => loadExports()}
          className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50 w-fit disabled:opacity-50"
          disabled={loading}
        >
          <ArrowPathIcon className={`size-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white border rounded overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 font-semibold">File</th>
                <th className="px-4 py-3 font-semibold">Requested By</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Progress</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
                <th className="px-4 py-3 font-semibold">Error</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && exports.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan="8">
                    Loading downloads...
                  </td>
                </tr>
              )}

              {!loading && exports.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan="8">
                    No CRM exports found.
                  </td>
                </tr>
              )}

              {exports.map((job) => {
                const expired = isExpired(job);
                const canDownload = job.status === 'completed' && !expired;
                return (
                  <tr key={job.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{job.fileName || '-'}</div>
                      <div className="text-xs text-gray-500">
                        App Users CSV {job.fileSize ? `- ${formatFileSize(job.fileSize)}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>{job.requestedBy?.email || job.requestedBy?.uid || '-'}</div>
                      {job.requestedBy?.email && (
                        <div className="text-xs text-gray-500">{job.requestedBy.uid}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{formatDate(job.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${statusClasses(job.status, expired)}`}>
                        {activeStatuses.has(job.status) && <ArrowPathIcon className="size-3 animate-spin" />}
                        {job.status === 'failed' && <ExclamationTriangleIcon className="size-3" />}
                        {expired ? 'Expired' : statusLabel(job.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{progressText(job)}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(job.expiresAt)}</td>
                    <td className="px-4 py-3 text-sm text-red-700 max-w-xs">
                      <div className="line-clamp-2">{job.error || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <PermissionGate permission="crm.users.download"><button
                        type="button"
                        onClick={() => handleDownload(job)}
                        className="border px-3 py-2 rounded inline-flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50"
                        disabled={!canDownload || downloadingId === job.id}
                      >
                        {downloadingId === job.id ? (
                          <ArrowPathIcon className="size-4 animate-spin" />
                        ) : (
                          <ArrowDownTrayIcon className="size-4" />
                        )}
                        Download
                      </button></PermissionGate>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CrmDownloads;
